/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityCoverage.module.scss';

const REGISTRIES = [
  { code: 'gb', name: 'Companies House', country: 'United Kingdom' },
  { code: 'ie', name: 'Companies Registration Office', country: 'Ireland' },
  { code: 'fr', name: 'Registre du commerce et des sociétés', country: 'France' },
  { code: 'de', name: 'Handelsregister', country: 'Germany' },
  { code: 'nl', name: 'KVK Handelsregister', country: 'Netherlands' },
  { code: 'be', name: 'Banque-Carrefour des Entreprises', country: 'Belgium' },
  { code: 'lu', name: 'Registre de Commerce et des Sociétés', country: 'Luxembourg' },
  { code: 'es', name: 'Registro Mercantil', country: 'Spain' },
  { code: 'it', name: 'Registro Imprese', country: 'Italy' },
  { code: 'pt', name: 'Registo Comercial', country: 'Portugal' },
  { code: 'at', name: 'Firmenbuch', country: 'Austria' },
  { code: 'ch', name: 'Zefix · Handelsregister', country: 'Switzerland' },
  { code: 'pl', name: 'Krajowy Rejestr Sądowy', country: 'Poland' },
  { code: 'se', name: 'Bolagsverket', country: 'Sweden' },
  { code: 'no', name: 'Brønnøysundregistrene', country: 'Norway' },
  { code: 'dk', name: 'Det Centrale Virksomhedsregister', country: 'Denmark' },
  { code: 'fi', name: 'Patentti- ja rekisterihallitus', country: 'Finland' },
  { code: 'cy', name: 'Registrar of Companies', country: 'Cyprus' },
  { code: 'us', name: 'Secretaries of State · SEC EDGAR', country: 'United States' },
  { code: 'ca', name: 'Corporations Canada', country: 'Canada' },
  { code: 'sg', name: 'ACRA', country: 'Singapore' },
  { code: 'hk', name: 'Companies Registry', country: 'Hong Kong' },
  { code: 'au', name: 'ASIC', country: 'Australia' },
  { code: 'in', name: 'Ministry of Corporate Affairs', country: 'India' },
];

export const DefenceSecurityCoverage = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.08 });

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('400+ registries. Here are twenty-four of them.')}
          description={t('Every record on this page comes from a named government source.')}
        />
      </div>

      <div className={cn(styles.grid, revealed && styles.gridIn)} ref={ref}>
        {REGISTRIES.map((registry, i) => (
          <div className={styles.item} style={{ transitionDelay: `${(i % 8) * 35}ms` }} key={registry.name}>
            <span className={styles.flagBadge}>
              <Flag code={registry.code} className={styles.flag} />
            </span>
            <span className={styles.info}>
              <b>{registry.name}</b>
              <span>{registry.country}</span>
            </span>
          </div>
        ))}
      </div>

      <div className={styles.foot}>
        <span className={styles.footNote}>
          {t('Twenty-four shown. The full list runs to 400+ across 200+ countries.')}
        </span>
        <Button type="dark-outline" size="lg" route="/data-dictionary">
          {t('See the full coverage list')}
        </Button>
      </div>
    </Section>
  );
};
